import React from 'react';

/**
 * Sparkline SVG ultra-léger.
 *
 * Props :
 * - data : number[] (au moins 2 points)
 * - color : couleur du tracé (CSS, défaut 'currentColor')
 * - height, width : dimensions en pixels (par défaut 120x32)
 * - fill : si true, remplit la zone sous la courbe avec un dégradé translucide
 * - strokeWidth : épaisseur du trait (défaut 2)
 */
export default function Sparkline({
  data = [],
  color = 'currentColor',
  height = 32,
  width = 120,
  fill = true,
  strokeWidth = 1.6,
  ariaLabel = 'Évolution sur 7 jours',
}) {
  if (!Array.isArray(data) || data.length < 2) {
    return (
      <div
        className="w-full opacity-40"
        style={{ height }}
        aria-label={ariaLabel}
      />
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const padTop = 2;
  const padBottom = 2;
  const usableH = height - padTop - padBottom;

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = padTop + usableH - ((v - min) / range) * usableH;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => (i === 0 ? `M${p.x.toFixed(1)},${p.y.toFixed(1)}` : `L${p.x.toFixed(1)},${p.y.toFixed(1)}`))
    .join(' ');

  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      className="block"
    >
      {fill && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.45" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {fill && <path d={areaPath} fill={`url(#${gradId})`} />}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Point final mis en évidence */}
      <circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r={2.5}
        fill={color}
      />
    </svg>
  );
}
